import React, { useEffect } from "react";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import { BookOpen, Users, Palette, Landmark, ArrowRight, ListChecks } from "lucide-react";
import { generateBreadcrumbSchema, generateFAQSchema } from "@/utils/schemaMarkup";

const FloridaHighwaymen: React.FC = () => {
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Florida Highwaymen", url: "/florida-highwaymen" }
  ]);

  const faqSchema = generateFAQSchema([
    {
      question: "What are the Florida Highwaymen?",
      answer: "The Florida Highwaymen were a group of 26 African American landscape painters who emerged in the mid-20th century. Despite facing segregation and limited access to traditional galleries, these artists developed a direct-to-collector sales approach, selling their vivid Florida landscapes door-to-door and along the highway."
    },
    {
      question: "How many original Florida Highwaymen artists were there?",
      answer: "There were 26 original Florida Highwaymen artists recognized for their contributions to this unique art movement. Each artist developed their own distinctive style while maintaining the characteristic bold palette-knife techniques and vibrant Florida landscape themes."
    },
    {
      question: "How can I identify authentic Florida Highwaymen paintings?",
      answer: "Authentic Florida Highwaymen paintings typically feature vivid palette-knife skies, Florida coastal and Everglades settings, oil on Upson board or canvas, and distinctive artist signatures. Professional authentication by certified experts is recommended for verification."
    },
    {
      question: "Where can I buy Florida Highwaymen paintings?",
      answer: "You can buy authentic Florida Highwaymen paintings through reputable auction houses like Florida Highwaymen Auction LLC, which specializes in authentication, appraisal, and auction of original artworks by the legendary Florida Highwaymen painters."
    }
  ]);

  useSEO({
    title: "Florida Highwaymen - History, Artists & Authentic Paintings Guide",
    description: "Complete guide to Florida Highwaymen: discover the history, original 26 artists, signature styles, collecting tips, and how to buy authentic paintings. Expert insights and auction information.",
    keywords: "Florida Highwaymen, Florida Highwaymen history, Florida Highwaymen artists, authentic Florida Highwaymen paintings, buy Florida Highwaymen art, Florida Highwaymen guide",
    canonicalUrl: "/florida-highwaymen",
    ogType: 'article',
    additionalSchema: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Florida Highwaymen - History, Artists & Authentic Paintings Guide",
      "description": "Complete guide to Florida Highwaymen: discover the history, original 26 artists, signature styles, collecting tips, and how to buy authentic paintings.",
      "image": "https://floridahighwaymenauction.com/lovable-uploads/a04e8ead-7f23-41b8-91a5-a2427d0afcb6.png",
      "datePublished": "2024-01-01",
      "dateModified": new Date().toISOString().split('T')[0],
      "author": {
        "@type": "Organization",
        "name": "Florida Highwaymen Auction LLC"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Florida Highwaymen Auction LLC",
        "logo": {
          "@type": "ImageObject",
          "url": "https://floridahighwaymenauction.com/lovable-uploads/a04e8ead-7f23-41b8-91a5-a2427d0afcb6.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://floridahighwaymenauction.com/florida-highwaymen"
      },
      "inLanguage": "en-US",
      "keywords": "Florida Highwaymen, Florida Highwaymen history, Florida Highwaymen artists, authentic Florida Highwaymen paintings"
    }
  });

  // Add breadcrumb and FAQ schemas
  useEffect(() => {
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      breadcrumbScript.remove();
      faqScript.remove();
    };
  }, []);

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative">
        <div className="container mx-auto px-4 py-12">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center px-6 py-3 mb-6 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl">
              <BookOpen className="h-5 w-5 mr-2 text-emerald-400" />
              <span className="text-white font-light tracking-wide">Complete Guide</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-light text-white tracking-wide">
              Florida Highwaymen: History, Artists & Paintings
            </h1>
            <p className="text-white/80 mt-4 max-w-3xl mx-auto font-light leading-relaxed text-lg">
              Discover the complete story of the Florida Highwaymen - 26 legendary African American landscape artists who revolutionized American art. Learn about their history, signature styles, original artists, and how to collect authentic Florida Highwaymen paintings.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-white/5 backdrop-blur-xl border border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Landmark className="h-5 w-5 text-emerald-400" />
                    Origins & History
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-white/80 leading-relaxed font-light space-y-4">
                  <p>
                    The Florida Highwaymen were a group of African American landscape painters who emerged in the mid-20th century. Despite facing segregation and limited access to traditional galleries, these artists developed a direct-to-collector sales approach, selling their vivid Florida landscapes door-to-door and along the highway.
                  </p>
                  <p>
                    Their scenes feature royal poincianas, coastal rivers, palms, and dramatic skies—captured quickly with bold palette-knife techniques to meet demand. The movement is now recognized for its cultural importance and artistic innovation.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-xl border border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="h-5 w-5 text-emerald-400" />
                    Artists & Profiles
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-white/80 leading-relaxed font-light space-y-4">
                  <p>
                    The original circle spans multiple artists with distinct approaches and signatures. Explore biographies, key works, and auction histories.
                  </p>
                  <Link to="/artists" className="inline-flex items-center text-emerald-300 hover:text-emerald-200">
                    Browse Artist Profiles <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-xl border border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Palette className="h-5 w-5 text-emerald-400" />
                    Styles, Materials, & Signatures
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-white/80 leading-relaxed font-light space-y-4">
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Vibrant palette-knife skies; rapid application for immediacy</li>
                    <li>Everglades, Indian River, and Atlantic coastal settings</li>
                    <li>Oil on Upson board or canvas; handmade frames</li>
                    <li>Signatures vary by artist; consult reference examples</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-xl border border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ListChecks className="h-5 w-5 text-emerald-400" />
                    Collecting & Authentication Tips
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-white/80 leading-relaxed font-light space-y-4">
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Request provenance and condition reports for each lot</li>
                    <li>Examine substrate (Upson board/canvas) and frame construction</li>
                    <li>Compare signatures to authenticated exemplars</li>
                    <li>Work with specialists; rely on curated auctions for guarantees</li>
                  </ul>
                  <div className="flex gap-4">
                    <Link to="/appraisal" className="inline-flex items-center text-emerald-300 hover:text-emerald-200">
                      Request an Appraisal <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                    <Link to="/auctions" className="inline-flex items-center text-emerald-300 hover:text-emerald-200">
                      View Current Auctions <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right column */}
            <div className="space-y-6">
              <Card className="bg-white/5 backdrop-blur-xl border border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Quick Links</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Link to="/artworks" className="block text-emerald-300 hover:text-emerald-200">Browse Artworks</Link>
                  <Link to="/blog" className="block text-emerald-300 hover:text-emerald-200">Educational Blog</Link>
                  <Link to="/consign" className="block text-emerald-300 hover:text-emerald-200">Consign a Work</Link>
                  <Link to="/contact" className="block text-emerald-300 hover:text-emerald-200">Contact Specialists</Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FloridaHighwaymen;


